import { useRouter } from "next/router";
import { IToggle, useFlags } from "@unleash/nextjs";
import { useState } from "react";

import { hideOverrideFeatureFlagsModal } from "@chef/state-management";
import { useAppDispatch, useAppSelector } from "@chef/state-management/hooks";
import { Button, Disclosure, Switch } from "@chef/components";
import { isEmptyArray } from "@chef/utils/array";

import { Modal } from "@chef/smart-components";
import { useFeatureFlagOverrides } from "./useFeatureFlagOverrides";

interface FeatureFlagProps {
  name: string;
  value: boolean;
  onClick: () => void;
}

interface FeatureFlagsProps {
  initialOpen: boolean;
  prefix: string;
  flags: IToggle[];
  overrides: Record<string, boolean>;
  onFlagToggle: (flagName: string, value: boolean) => void;
}

const orderFlags = (flags: IToggle[]) => {
  const orderedFlags: Record<string, IToggle[]> = { misc: [] };

  for (const flag of flags) {
    const prefix = flag.name.split("_")[0];
    const hasUnderscore = flag.name.includes("_");

    if (!hasUnderscore) {
      orderedFlags["misc"].push(flag);
      continue;
    }

    if (!orderedFlags[prefix]) {
      orderedFlags[prefix] = [];
    }

    orderedFlags[prefix].push(flag);
  }

  return orderedFlags;
};

const getSortOrder = (prefixes: string[]) => {
  const sortOrder = ["global", "amk", "gl", "lmk", "rn"];

  for (const prefix of prefixes) {
    if (!sortOrder.includes(prefix)) {
      sortOrder.push(prefix);
    }
  }

  return sortOrder;
};

const FeatureFlag = ({ name, value, onClick }: FeatureFlagProps) => {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="select-none">{name}</span>
      <Switch checked={value} onChange={onClick} small />
    </div>
  );
};

const FeatureFlags = ({
  initialOpen,
  prefix,
  flags,
  overrides,
  onFlagToggle,
}: FeatureFlagsProps) => {
  const [isOpen, setIsOpen] = useState(initialOpen);

  return (
    <Disclosure
      open={isOpen}
      Title={() => (
        <h2 className="text-lg">
          <strong>{prefix}</strong>
        </h2>
      )}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {flags.map((flag) => {
        const value =
          flag.name in overrides ? overrides[flag.name] : flag.enabled;

        return (
          <FeatureFlag
            key={flag.name}
            name={flag.name}
            value={value}
            onClick={() => onFlagToggle(flag.name, value)}
          />
        );
      })}
    </Disclosure>
  );
};

export const OverrideFeatureFlagsModal = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const { open } = useAppSelector((state) => state.modal.overrideFeatureFlags);

  const [isDirty, setDirty] = useState(false);

  const { overrides, setOverride, reset } = useFeatureFlagOverrides();

  const orderedFlags = orderFlags(flags);
  const sortOrder = getSortOrder(Object.keys(orderedFlags));

  return (
    <Modal
      open={open}
      panel="card"
      onClose={() => {
        dispatch(hideOverrideFeatureFlagsModal());

        if (isDirty) {
          router.reload();
        }
      }}
      animation="fly-up"
      interactions={[
        ["top-close-button", "enabled"],
        ["pull-down", "mobile-only"],
      ]}
    >
      <h1 className="mb-4 text-xl">
        <strong>Feature flag overrides</strong>
      </h1>

      <p className="mb-4 text-sm">
        <Button
          outlined
          onClick={() => {
            reset();
            router.reload();
          }}
          tiny
        >
          Reset overrides
        </Button>
      </p>

      <div className="flex flex-col gap-4">
        {sortOrder.map((prefix) => {
          const flags = orderedFlags[prefix] || [];

          if (isEmptyArray(flags)) {
            return null;
          }

          return (
            <FeatureFlags
              key={prefix}
              initialOpen={prefix === "global"}
              prefix={prefix}
              flags={flags}
              overrides={overrides}
              onFlagToggle={(flagName, value) => {
                setOverride(flagName, !value);
                setDirty(true);
              }}
            />
          );
        })}
      </div>
    </Modal>
  );
};
